import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, Grid, LL_H_C, LL_V_L, openContent } from '@nu-art/thunderstorm-frontend/index';
import { FindingType_Enumerated, FindingType_Numeric, FindingType_Textual } from '@app/advisor-shared';
import { dbObjectToId, filterDuplicates, filterInstances, sortArray } from '@nu-art/ts-common';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import './Component_Cockpit_KeyFindings.scss';
import { AdvisorConfigKeyFE_FindingView_Cockpit, ModuleFE_Advisor_MeasurementUnit, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { getFindingsInContext } from '../../../../_utils/get-findings-in-context.js';
import { Tooltip_Finding_Enum, Tooltip_Finding_Numeric, Tooltip_Finding_Textual } from '@app/advisor-ui-frontend';
import { tooltipFindingsTranslation } from '../../../panels/AdvisorPanel_Findings/utils/tooltip-findings.js';
import { RelatedFinding } from '../../../../_utils/RelatedFinding.js';
import { valueID_No } from '@app/hcs-shared';
import { advisorConfig_HideNegative, ModuleFE_AdvisorSettings } from '../../../../modules/ModuleFE_AdvisorSettings/index.js';
export class Component_Cockpit_KeyFindings extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.focusedDiagnosisId = nextProps.focusedDiagnosisId;
        const protocols = state.focusedDiagnosisId
            ? nextProps.protocols.filter(p => p.protocol.variableIds.includes(state.focusedDiagnosisId))
            : nextProps.protocols;
        //Collect Findings
        const ddxRelatedFindings = this.getProtocolRelatedFindings(protocols, state.focusedDiagnosisId);
        const ddxRFIds = ddxRelatedFindings.map(rf => rf.finding.variableId);
        const hpiRelatedFindings = this.getHPIFindings(ddxRFIds);
        const specialAttentionDiseaseFindings = this.getSpecialAttentionDiseases(ddxRFIds);
        state.relatedFindings = {
            ddx: sortArray(ddxRelatedFindings, rf => rf.variable._nameInLowercase),
            hpi: sortArray(hpiRelatedFindings, rf => rf.variable._nameInLowercase),
            specialAttention: sortArray(specialAttentionDiseaseFindings, rf => rf.variable._nameInLowercase),
        };
        return state;
    }
    //######################### Logic ##########################
    getProtocolRelatedFindings(protocols, diagnosisId) {
        const diagnosisIds = diagnosisId ? [diagnosisId] : undefined;
        const diagnosisData = protocols.map(p => p.getDiagnosisData(diagnosisIds)).flat();
        return diagnosisData.reduce((rfs, data) => {
            return data.getRelatedFindings(rfs);
        }, []);
    }
    getHPIFindings(existingFindingIds) {
        const config = AdvisorConfigKeyFE_FindingView_Cockpit.get();
        if (!config?.hpiContextRuleIds)
            return [];
        let runtimeFindings = config.hpiContextRuleIds.reduce((findings, contextRuleId) => {
            const runtimeFindings = getFindingsInContext(contextRuleId)
                .filter(finding => !existingFindingIds.includes(finding.variableId));
            return filterDuplicates([...findings, ...runtimeFindings], dbObjectToId);
        }, []);
        if (ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_HideNegative))
            runtimeFindings = runtimeFindings.filter(finding => finding.type !== 'enumerated' || finding.valueIds[0] !== valueID_No);
        return filterInstances(runtimeFindings.map(rtf => RelatedFinding.createNew(rtf)));
    }
    getSpecialAttentionDiseases(existingFindingIds) {
        let runtimeFindings = ModuleFE_RuntimeFinding
            .getLatestFindings(false, false)
            .filter(rtf => ModuleFE_RuntimeFinding.getSpecialAttention(rtf.variableId).disease && !existingFindingIds.includes(rtf._id));
        if (ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_HideNegative))
            runtimeFindings = runtimeFindings.filter(finding => finding.type !== 'enumerated' || finding.valueIds[0] !== valueID_No);
        return filterInstances(runtimeFindings.map(rtf => RelatedFinding.createNew(rtf)));
    }
    getTooltipComponent = (relatedFinding, showTooltip) => {
        if (!showTooltip)
            return;
        const findingType = relatedFinding.finding.type;
        switch (findingType) {
            case FindingType_Enumerated:
                return Tooltip_Finding_Enum;
            case FindingType_Numeric:
                return Tooltip_Finding_Numeric;
            case FindingType_Textual:
                return Tooltip_Finding_Textual;
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'cockpit-panel cockpit-key-findings', children: [this.render_Header(), this.render_KeyFindings()] });
    }
    render_Header = () => {
        return _jsx(LL_H_C, { className: 'cockpit-panel__header', children: "Key Findings" });
    };
    render_KeyFindings = () => {
        return _jsxs(FadeScrollWrapper, { children: [this.render_KeyFindings_SpecialAttentionDisease(), this.render_KeyFindings_HPIRelated(), this.render_KeyFindings_DDxRelated()] });
    };
    render_KeyFindings_DDxRelated = () => {
        const relatedFindings = this.state.relatedFindings.ddx;
        if (!relatedFindings.length)
            return;
        return _jsxs(Grid, { className: 'cockpit-key-findings__section', children: [_jsx(LL_H_C, { className: 'cockpit-key-findings__section-title', children: "DDx Related" }), relatedFindings.map(rf => this.render_RelatedFinding(rf))] });
    };
    render_KeyFindings_HPIRelated = () => {
        if (this.state.focusedDiagnosisId)
            return;
        const relatedFindings = this.state.relatedFindings.hpi;
        if (!relatedFindings.length)
            return;
        return _jsxs(Grid, { className: 'cockpit-key-findings__section hpi', children: [_jsx(LL_H_C, { className: 'cockpit-key-findings__section-title', children: "HPI Related" }), relatedFindings.map(rf => this.render_RelatedFinding(rf))] });
    };
    render_KeyFindings_SpecialAttentionDisease = () => {
        if (this.state.focusedDiagnosisId)
            return;
        const relatedFindings = this.state.relatedFindings.specialAttention;
        if (!relatedFindings.length)
            return;
        return _jsxs(Grid, { className: 'cockpit-key-findings__section', children: [_jsx(LL_H_C, { className: 'cockpit-key-findings__section-title', children: "Special Attention Diseases" }), relatedFindings.map(rf => this.render_RelatedFinding(rf))] });
    };
    render_RelatedFinding = (relatedFinding, showTooltip = true) => {
        const titleLabel = () => _jsx("div", { className: 'related-finding__title__label', children: relatedFinding.getLabel() });
        const Tooltip = this.getTooltipComponent(relatedFinding, showTooltip);
        const tooltipData = tooltipFindingsTranslation(relatedFinding.findingResults);
        const indications = relatedFinding.getIndicativeDiagnosis();
        return _jsxs(React.Fragment, { children: [_jsxs(Grid, { className: 'related-finding', children: [_jsxs(LL_H_C, { className: 'related-finding__title', children: [Tooltip
                                    ? _jsx(Tooltip, { data: tooltipData, modules: {
                                            variable: ModuleFE_Advisor_Variable,
                                            value: ModuleFE_Advisor_Value,
                                            measurementUnit: ModuleFE_Advisor_MeasurementUnit
                                        }, children: titleLabel() })
                                    : titleLabel(), !!indications?.length &&
                                    _jsx(ICONS.advisor_v5_indicative.component, { ...openContent.tooltip.top('related-finding-indication', () => this.render_IndicationText(indications), { offset: 5 }) })] }), _jsx("div", { className: 'related-finding__value', children: relatedFinding.getValue() }), _jsx(LL_H_C, { className: 'related-finding__dots', children: relatedFinding.getRelatedDiagnosis()?.map(diagnosisRelation => _jsx("div", { style: { background: diagnosisRelation.diagnosis.getColor() }, ...openContent.tooltip.top('related-findings__tooltip', diagnosisRelation.diagnosis.getLabel(), { offset: 5 }) }, diagnosisRelation.diagnosis.diagnosis._id)) })] }), relatedFinding.getAttributes()?.map(att => this.render_RelatedFinding(att, false))] }, relatedFinding.finding.variableId);
    };
    render_IndicationText = (indications) => {
        return indications.map(indication => `${indication.indication === 1 ? 'Indicative' : 'Non-Indicative'} for ${indication.diagnosis.getLabel()}`).join(',\n');
    };
}
//# sourceMappingURL=Component_Cockpit_KeyFindings.js.map